import useSWRMutation from "swr/mutation";
import { Network } from "@/utils/network";
import { zodUnsafeValidate } from "@/utils/zod";
import { InstagramSearch } from "../types";
import {
  getInstagramSearchInfluencersFilters,
  getInstagramSearchProfilesFilters,
  useInstagramSearch,
} from "./useInstagramSearch";
import {
  useInstagramSearchList,
  type InstagramSearchListResponse,
} from "./useInstagramSearchList";
import {
  useInstagramSearchLists,
  type InstagramSearchListsResponse,
} from "./useInstagramSearchLists";
import {
  useInstagramSearchListId,
  useIsInstagramSearchInfluencers,
} from "./useInstagramSearchRouter";

interface InstagramSearchAssignListProps {
  arg: {
    type: "assign" | "toggle";
    list: InstagramSearch.List;
    profiles: Set<InstagramSearch.Profile["id"]>;
  };
}

export const useInstagramSearchAssignList = () => {
  const config = useInstagramSearch((state) => state.config);
  const clearSelection = useInstagramSearch((state) => state.clearSelection);
  const listsClearSelection = useInstagramSearch(
    (state) => state.listsClearSelection,
  );
  const { mutate: mutateLists } = useInstagramSearchLists();
  const { mutate: mutateList } = useInstagramSearchList();
  const listId = useInstagramSearchListId();
  const isInfluencers = useIsInstagramSearchInfluencers();

  return useSWRMutation(
    config.api.addToList,
    async (api, { arg: props }: InstagramSearchAssignListProps) => {
      const filters =
        props.profiles.size === 0
          ? isInfluencers
            ? getInstagramSearchInfluencersFilters()
            : getInstagramSearchProfilesFilters()
          : {};

      const { data } = await Network.post<InstagramSearch.List>(api, {
        type: props.type,
        id: props.list.id,
        profiles: Array.from(props.profiles),
        ...filters,
      });

      return {
        type: props.type,
        list: zodUnsafeValidate(InstagramSearch.ListSchema, data),
        profiles: props.profiles,
      };
    },
    {
      onSuccess: (props) => {
        mutateLists((data) => getUpdatedLists(data, props), false);

        if (props.type === "toggle" && props.list.id.toString() === listId) {
          mutateList((data) => getUpdatedList(data, props), false);
        }

        clearSelection();
        listsClearSelection();
      },
    },
  );
};

const getUpdatedLists = (
  data: InstagramSearchListsResponse | undefined,
  props: {
    type: "assign" | "toggle";
    list: InstagramSearch.List;
  },
): InstagramSearchListsResponse | undefined => {
  if (data === undefined) {
    return undefined;
  }

  return {
    ...data,
    lists: data.lists.map((list) => {
      if (list.id === props.list.id) {
        return props.list;
      }

      return list;
    }),
  };
};

const getUpdatedList = (
  data: InstagramSearchListResponse[] | undefined,
  props: {
    profiles: Set<string | number>;
  },
): InstagramSearchListResponse[] | undefined => {
  if (data === undefined) {
    return undefined;
  }

  return data.map((data) => {
    return {
      ...data,
      profiles: data.profiles.filter(
        (profile) => props.profiles.has(profile.id) === false,
      ),
    };
  });
};
